/**
 * Presenting a rate honestly.
 *
 * Two rules from the product requirements live here. A small nonzero rate must
 * never render as "$0.00" — that is a lie about a real price. And free,
 * unknown, included and uncovered must be distinguishable without relying on
 * colour, so every state carries a word, not just a hue.
 */

import {
  canonicalizeDecimal,
  type Rate,
  type RateSet,
  type TokenBucket,
  type TokenLimits,
} from "@pricing/contracts";

export interface RateDisplay {
  /** The text a screen reader and a monochrome screen both get. */
  readonly label: string;
  readonly title: string;
  readonly kind: "priced" | "free" | "included" | "unknown" | "unsupported" | "not_billed";
}

/**
 * Render a decimal amount exactly as published, never through a float: a rate
 * of 0.175 is "$0.175", not a rounded "$0.17", and so a nonzero value can never
 * read as "$0.00". Trailing zeros are trimmed down to cents, which also turns
 * an estimate's fixed-scale "4.50000000" into "$4.50" without changing it.
 */
export function formatUsd(amount: string): string {
  const negative = amount.startsWith("-");
  let digits: string;
  try {
    digits = canonicalizeDecimal(negative ? amount.slice(1) : amount);
  } catch {
    // Not a decimal literal: show what the server sent rather than guess.
    return `$${amount}`;
  }
  const [whole, fraction = ""] = digits.split(".");
  return `${negative ? "-" : ""}$${whole}.${fraction.padEnd(2, "0")}`;
}

export function describeRate(rate: Rate): RateDisplay {
  switch (rate.state) {
    case "priced":
      return rate.amount === "0"
        ? { label: "free", title: "Explicitly free: the source publishes a zero rate.", kind: "free" }
        : {
            label: formatUsd(rate.amount),
            title: `${rate.amount} USD per million tokens`,
            kind: "priced",
          };
    case "included":
      return {
        label: `included in ${rate.in_bucket.replace(/_/g, " ")}`,
        title: `Billed through the ${rate.in_bucket} rate under billing rule ${rate.billing_rule_id}.`,
        kind: "included",
      };
    case "unsupported":
      if (rate.reason_code === "openai_prompt_caching_not_available") {
        return {
          label: "not available",
          title: "This OpenAI model does not offer prompt caching.",
          kind: "unsupported",
        };
      }
      if (rate.reason_code === "openai_cached_input_not_available_for_offering") {
        return {
          label: "not available",
          title: "OpenAI publishes no cached-input rate for this model, service tier and prompt size.",
          kind: "unsupported",
        };
      }
      return {
        label: "not covered",
        title: `This product does not cover this charge: ${rate.reason_code}.`,
        kind: "unsupported",
      };
    case "not_billed":
      return {
        label: "not billed",
        title: `This seller has no charge of this kind: ${rate.reason_code}.`,
        kind: "not_billed",
      };
    default:
      return {
        label: "unknown",
        title: `No evidence for this rate: ${rate.reason_code}.`,
        kind: "unknown",
      };
  }
}

export const BUCKET_LABELS: Readonly<Record<TokenBucket, string>> = {
  input: "Input",
  output: "Output",
  reasoning: "Reasoning",
  cache_read: "Cache read",
  cache_write: "Cache write",
  cache_write_5m: "Cache write 5m",
  cache_write_1h: "Cache write 1h",
};

export function formatInstant(value: string | null): string {
  if (!value) return "—";
  return value.replace("T", " ").replace(".000Z", "Z");
}

/**
 * How long ago, by the wall clock. Ages were once measured to the catalog's
 * observation cutoff, which stands still while collection is stopped: a price
 * last accepted five days ago read "2 d ago", because the last run was three
 * days ago. Age is the question "how old is this now?".
 */
export function ageNow(from: string | null): string {
  return relativeAge(from, new Date().toISOString());
}

export function relativeAge(from: string | null, to: string): string {
  if (!from) return "never";
  const ms = Date.parse(to) - Date.parse(from);
  if (!Number.isFinite(ms)) return "unknown";
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return "under an hour ago";
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** A wait, rounded up to the minute and never overstated by more than that. */
export function timeUntil(to: string, now: number = Date.now()): string {
  const ms = Date.parse(to) - now;
  if (!Number.isFinite(ms) || ms <= 0) return "under a minute";
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function rateSetSummary(rateSet: RateSet): RateDisplay[] {
  return (Object.keys(BUCKET_LABELS) as TokenBucket[]).map((bucket) => describeRate(rateSet[bucket]));
}

export function formatPercent(value: string | null, label: string | null): string {
  if (value === null) return label === "from_free" ? "from free" : "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return `${numeric > 0 ? "+" : ""}${numeric.toFixed(2)}%`;
}

function tokens(count: number): string {
  if (count >= 1_000_000 && count % 1_000_000 === 0) return `${count / 1_000_000}M`;
  if (count >= 1_000 && count % 1_000 === 0) return `${count / 1_000}K`;
  return count.toLocaleString();
}

/**
 * A context band as a person reads it. Bands are half-open, so [0, 272001)
 * is "≤ 272K" and [272001, ∞) is "> 272K".
 */
export function formatContextBand(band: { min_tokens: number; max_tokens: number | null }): string {
  if (band.min_tokens === 0 && band.max_tokens === null) return "any length";
  if (band.min_tokens === 0 && band.max_tokens !== null) return `≤ ${tokens(band.max_tokens - 1)} input`;
  if (band.max_tokens === null) return `> ${tokens(band.min_tokens - 1)} input`;
  return `${tokens(band.min_tokens)}–${tokens(band.max_tokens - 1)} input`;
}

const COMPACT_TOKENS = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumSignificantDigits: 3,
});

export interface LimitLine {
  /** Compact, for a table cell: "1.05M context". */
  readonly short: string;
  /** Exact, for a page or a tooltip: "1,050,000-token context window". */
  readonly long: string;
}

/**
 * A source's token limits in words, one line per limit it states. Each says
 * which limit it is, because sources state different ones: a context window
 * counts input and output together, a maximum input does not.
 */
export function describeLimits(limits: TokenLimits | null): LimitLine[] {
  if (!limits) return [];
  const lines: LimitLine[] = [];
  const add = (value: number | null, short: string, long: string): void => {
    if (value === null) return;
    lines.push({
      short: `${COMPACT_TOKENS.format(value)} ${short}`,
      long: `${value.toLocaleString("en-US")}-token ${long}`,
    });
  };
  add(limits.context_window_tokens, "context", "context window");
  add(limits.max_input_tokens, "input", "maximum input");
  add(limits.max_output_tokens, "output", "maximum output");
  return lines;
}
